import React, { useCallback, useEffect, useState } from "react";
import axios from "../../utils/axiosInstance";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useT } from "../../contexts/TranslationContext";
import { formatExpectedCostKr, getTaskLabel } from "./expectedCostUtils";

/** Compact panel for Invoice Inbox — settle open accruals against an invoice. */
export default function ExpectedMaterialCostsSection({
  projectId,
  taskBundle,
  onChanged,
  matchedInvoiceId = null,
  defaultCollapsed = false,
}) {
  const { t } = useT();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [accruals, setAccruals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionId, setActionId] = useState(null);

  const fetchAccruals = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`/projects/${projectId}/material-accruals`, {
        params: { status: "open", includeSettledLimit: 0 },
      });
      setAccruals(res.data?.accruals || []);
    } catch (err) {
      console.error("Failed to load material accruals", err);
      setError(err?.response?.data?.message || t("projectDetails.expectedCostsLoadError"));
      setAccruals([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  useEffect(() => {
    fetchAccruals();
  }, [fetchAccruals]);

  const openAccruals = accruals.filter((a) => a.status === "open");

  async function handleSettle(accrualId) {
    if (!projectId || !window.confirm(t("projectDetails.expectedCostsSettleConfirm"))) return;
    setActionId(accrualId);
    setError("");
    try {
      await axios.post(`/projects/${projectId}/material-accruals/${accrualId}/settle`, {
        ...(matchedInvoiceId ? { matchedInvoiceId } : {}),
      });
      await fetchAccruals();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.message || t("projectDetails.expectedCostsSettleError"));
    } finally {
      setActionId(null);
    }
  }

  if (!projectId) return null;

  return (
    <div className="border border-gray-200 rounded-md p-3 bg-gray-50/50">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <button
          type="button"
          className="flex items-center gap-2 text-left font-semibold text-gray-900 hover:text-gray-700 text-sm"
          onClick={() => setCollapsed((c) => !c)}
        >
          {collapsed ? <FaChevronDown className="w-3 h-3" /> : <FaChevronUp className="w-3 h-3" />}
          {t("projectDetails.expectedCostsTitle")}
          {openAccruals.length > 0 && (
            <span className="text-xs font-normal text-gray-500">
              ({openAccruals.length} {t("projectDetails.expectedCostsOpen")})
            </span>
          )}
        </button>
      </div>

      {!collapsed && (
        <>
          {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
          {loading ? (
            <p className="text-sm text-gray-500">{t("projectDetails.expectedCostsLoading")}</p>
          ) : openAccruals.length === 0 ? (
            <p className="text-sm text-gray-600">{t("projectDetails.expectedCostsEmpty")}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {openAccruals.map((a) => (
                <li
                  key={a._id}
                  className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900">{a.description}</div>
                    <div className="text-xs text-gray-500">
                      {getTaskLabel(a.taskOverride, taskBundle)}
                      {a.supplierName ? ` · ${a.supplierName}` : ""}
                      {" · "}
                      {formatExpectedCostKr(a.amount)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="text-sm text-brand hover:underline disabled:opacity-50 shrink-0"
                    disabled={actionId === a._id}
                    onClick={() => handleSettle(a._id)}
                  >
                    {matchedInvoiceId
                      ? t("projectDetails.expectedCostsSettleWithInvoice")
                      : t("projectDetails.expectedCostsSettle")}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
